import styled from '@emotion/styled';
import type { FunnelOffer, StepPageContent, StepPageBlock, DisplaySettings } from '../types';
import type { BlockCallbacks } from './ComponentRenderers';
import { normalizeBlocks, renderBlocks, blocksHaveCta } from './BlockRenderer';
import { withResolvedFunnelOfferPrice } from '../offer-pricing';

interface FunnelPageContentProps {
  offer: FunnelOffer;
  offers?: FunnelOffer[];
  selectedOfferId?: string;
  onSelectOffer?: (offerId: string) => void;
  pageContent?: StepPageContent | StepPageBlock[] | null;
  errorMessage?: string | null;
  accepting: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onDismiss: () => void;
  /**
   * Width tier for the rendered page: 'card' (centered, ~920px), 'medium'
   * (half-width, ~1400px — the regular upsell/downsell treatment), or
   * 'full_width' (edge-to-edge, no chrome). Defaults to 'card' unless
   * `fullPage` is set (legacy boolean, kept for existing callers).
   */
  layoutMode?: 'card' | 'medium' | 'full_width';
  /** @deprecated Use `layoutMode="full_width"` instead. Kept for existing callers (e.g. PostPurchasePage). */
  fullPage?: boolean;
  displaySettings?: DisplaySettings | null;
  suppressOfferActions?: boolean;
  /**
   * `experienceType: "page"` as rendered by `FunnelExperience` (product page,
   * collection, cart, etc.) is a fullscreen takeover — like a modal, but
   * covering the entire viewport so nothing from the host page (header, nav,
   * the underlying PDP content) shows through, and rendering only the
   * selected template/pageContent (no dismiss button, no offer-switcher
   * chrome layered on top).
   *
   * Dedicated full-page routes that use `FunnelPageContent` as their entire
   * page (`/lp/:slug` landing pages, the post-purchase page) don't set this —
   * they keep the in-flow layout plus the dismiss button and offer selector
   * they already rely on.
   */
  takeoverOverlay?: boolean;
  /**
   * Host page embeds this experience in-flow (e.g. cart above native line
   * items). Keeps authored `layoutMode` (including `full_width`) but skips
   * fixed takeover / min-height:100vh so host content stays visible under the
   * page blocks.
   */
  inlineHost?: boolean;
}

const WIDTH_BY_LAYOUT_MODE: Record<'card' | 'medium' | 'full_width', string> = {
  card: '920px',
  medium: '1400px',
  full_width: '100vw',
};

const OverlayBackdrop = styled.div<{ $centerContent: boolean }>`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  justify-content: center;
  align-items: ${({ $centerContent }) => ($centerContent ? 'safe center' : 'stretch')};
`;

const Wrapper = styled.section<{
  $layoutMode: 'card' | 'medium' | 'full_width';
  $takeoverOverlay: boolean;
  $inlineHost: boolean;
}>`
  width: ${({ $layoutMode, $inlineHost, $takeoverOverlay }) => {
    // Cart (inlineHost) already sits in a full-bleed host — use 100%, not 100vw
    // (100vw + scrollbar causes horizontal overflow).
    if ($inlineHost && $layoutMode === 'full_width') return '100%';
    if (!$takeoverOverlay && $layoutMode === 'full_width') return '100vw';
    return '100%';
  }};
  max-width: ${({ $layoutMode, $inlineHost, $takeoverOverlay }) => {
    if ($inlineHost && $layoutMode === 'full_width') return 'none';
    if ($takeoverOverlay && $layoutMode === 'full_width') return WIDTH_BY_LAYOUT_MODE.full_width;
    return WIDTH_BY_LAYOUT_MODE[$layoutMode];
  }};
  margin: ${({ $layoutMode, $takeoverOverlay, $inlineHost }) => {
    // Inline cart: authored template spans the host; native chrome stays in CartBody.
    if ($inlineHost && $layoutMode === 'full_width') return '0 0 24px';
    if ($inlineHost) return '0 auto 24px';
    if ($takeoverOverlay) return '0 auto';
    return $layoutMode === 'full_width' ? '0' : '16px auto';
  }};
  background: ${({ $layoutMode, theme }) =>
    $layoutMode === 'full_width' ? 'transparent' : theme.colors.background.primary};
  border: ${({ $layoutMode, $takeoverOverlay, theme }) =>
    $takeoverOverlay || $layoutMode === 'full_width' ? 'none' : `1px solid ${theme.colors.border.color}`};
  border-radius: ${({ $layoutMode, $takeoverOverlay, theme }) =>
    $takeoverOverlay || $layoutMode === 'full_width' ? '0' : theme.radius.regular};
  overflow: hidden;
  box-sizing: border-box;
  ${({ $layoutMode, $takeoverOverlay, $inlineHost }) =>
    !$inlineHost && $takeoverOverlay && $layoutMode === 'full_width' && 'min-height: 100vh;'}
  ${({ $layoutMode, $takeoverOverlay, $inlineHost }) =>
    !$inlineHost &&
    !$takeoverOverlay &&
    $layoutMode === 'full_width' &&
    'flex: 1; min-height: 100vh; display: flex; flex-direction: column; position: relative;'}
  ${({ $inlineHost, $layoutMode }) =>
    $inlineHost &&
    $layoutMode === 'full_width' &&
    'display: flex; flex-direction: column; position: relative;'}
`;

const Header = styled.div<{ $layoutMode: 'card' | 'medium' | 'full_width' }>`
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  ${({ $layoutMode }) => $layoutMode === 'full_width' && 'position: absolute; top: 0; right: 0; z-index: 2;'}
`;

const DismissButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  font-size: 16px;
`;

const Content = styled.div<{
  $layoutMode: 'card' | 'medium' | 'full_width';
}>`
  padding: ${({ $layoutMode }) => {
    // full_width = edge-to-edge authored page (same as landing takeover).
    // Do not inset cart inline hosts — gutters made the template look like a card.
    if ($layoutMode === 'full_width') return '0';
    return '20px';
  }};
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  width: 100%;
  box-sizing: border-box;
`;

const BlockCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: 14px;
`;

const OfferImage = styled.img`
  width: 100%;
  max-height: 240px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 24px;
  line-height: 1.2;
`;

const Text = styled.p`
  margin: 0;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OldPrice = styled.span`
  text-decoration: line-through;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NewPrice = styled.span`
  font-size: 24px;
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AcceptButton = styled.button`
  width: 100%;
  border: none;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.regular};
  font-weight: 700;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
`;

const DeclineButton = styled.button`
  border: none;
  background: transparent;
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const OfferSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const OfferSelectorButton = styled.button<{ $selected: boolean }>`
  border: 1px solid ${({ theme, $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.border.color};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.background.primary};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.background.primary : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

function formatPrice(value: number | null | undefined): string {
  if (!Number.isFinite(value)) return '$0.00';
  return `$${(value as number).toFixed(2)}`;
}

export function FunnelPageContent({
  offer,
  offers,
  selectedOfferId,
  onSelectOffer,
  pageContent,
  errorMessage,
  accepting,
  onAccept,
  onDecline,
  onDismiss,
  layoutMode,
  fullPage,
  displaySettings: _displaySettings,
  suppressOfferActions,
  takeoverOverlay,
  inlineHost = false,
}: FunnelPageContentProps) {
  // displaySettings available for future page layout customization (alignment, max-width, etc.)
  void _displaySettings;
  const resolvedLayoutMode: 'card' | 'medium' | 'full_width' =
    layoutMode ?? (fullPage ? 'full_width' : 'card');
  const displayOffer = withResolvedFunnelOfferPrice(offer);
  const displayOffers = offers?.map(withResolvedFunnelOfferPrice);
  const blocks = normalizeBlocks(pageContent);
  const hasCtaBlock = blocksHaveCta(blocks);
  const callbacks: BlockCallbacks = {
    onAccept: suppressOfferActions ? undefined : onAccept,
    onDecline,
    accepting,
    suppressOfferActions,
  };
  const multiOffers = Array.isArray(offers) && offers.length > 1 ? offers : null;

  const body = (
    <Wrapper
      role={takeoverOverlay || resolvedLayoutMode === 'card' ? 'dialog' : 'region'}
      aria-modal={takeoverOverlay ? 'true' : undefined}
      aria-label="Funnel page experience"
      data-layout-mode={resolvedLayoutMode}
      data-inline-host={inlineHost ? 'true' : 'false'}
      $layoutMode={resolvedLayoutMode}
      $takeoverOverlay={Boolean(takeoverOverlay)}
      $inlineHost={Boolean(inlineHost)}
    >
      {!takeoverOverlay && !inlineHost && (
        <Header $layoutMode={resolvedLayoutMode}>
          <DismissButton type="button" aria-label="Dismiss offer" onClick={onDismiss}>
            ×
          </DismissButton>
        </Header>
      )}
      <Content $layoutMode={resolvedLayoutMode}>
        {!takeoverOverlay && multiOffers && onSelectOffer && (
          <OfferSelector aria-label="Offer selector">
            {multiOffers.map((item, index) => {
              const isSelected = (selectedOfferId ?? offer.id) === item.id || (!selectedOfferId && index === 0);
              return (
                <OfferSelectorButton
                  key={item.id}
                  type="button"
                  $selected={isSelected}
                  aria-pressed={isSelected}
                  onClick={() => onSelectOffer(item.id)}
                >
                  {item.name || item.headline || `Offer ${index + 1}`}
                </OfferSelectorButton>
              );
            })}
          </OfferSelector>
        )}
        {blocks.length > 0 ? (
          renderBlocks(blocks, displayOffer, callbacks, displayOffers)
        ) : (
          <BlockCard>
            {offer.imageUrl && <OfferImage src={offer.imageUrl} alt={offer.headline ?? 'Offer'} />}
            <Title>{offer.headline}</Title>
            <Text>{offer.description}</Text>
            <PriceRow>
              <OldPrice>{formatPrice(offer.originalPrice)}</OldPrice>
              <NewPrice>{formatPrice(displayOffer.pricingValue)}</NewPrice>
            </PriceRow>
          </BlockCard>
        )}
        {errorMessage && (
          <Text style={{ color: '#b42318', marginTop: 12 }}>
            {errorMessage}
          </Text>
        )}
        {!hasCtaBlock && (
          <Actions>
            {!suppressOfferActions && (
              <AcceptButton type="button" onClick={onAccept} disabled={accepting}>
                {accepting ? 'Adding...' : offer.ctaText}
              </AcceptButton>
            )}
            <DeclineButton type="button" onClick={onDecline} disabled={accepting}>
              {offer.declineText}
            </DeclineButton>
          </Actions>
        )}
      </Content>
    </Wrapper>
  );

  if (!takeoverOverlay) {
    return body;
  }

  return (
    <OverlayBackdrop $centerContent={resolvedLayoutMode !== 'full_width'}>
      {body}
    </OverlayBackdrop>
  );
}
